// BOT pledge maths — every rule here was checked against the team's own CaryPact
// orders (26 pledges, 65 reward days), not assumed:
//
//  * CaryPact pays the plan rate on the pledge PLUS the rewards already sitting in
//    the order. A real 50 BOT / 360-day pledge paid 0.300000 BOT on its first full
//    day and 0.316380 ten days later — that is 0.6%/day on a balance that
//    compounds, not 0.6%/day on the opening pledge.
//  * A FIXED term also hands back 1/term of the pledge EVERY DAY (100 BOT on a
//    360-day term unlocks 0.2778 BOT a day), and the rewards keep accruing on the
//    whole pledge while it unlocks. That is why "when do I have my money back" is
//    a completely different question for BOT than it is for CA.
//  * Flexible has no term and no unlock schedule — you can take it out any day.

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/** What one day's rate turns into over a year, compounded. */
export const effectiveApr = (ratePct) => (Math.pow(1 + num(ratePct) / 100, 365) - 1) * 100;

/** How much of a fixed pledge has unlocked by `day` (flexible locks nothing). */
export const releasedByDay = (pledge, termDays, day) => {
  const T = Math.round(num(termDays));
  if (T <= 0) return 0;
  return (num(pledge) * Math.min(Math.max(day, 0), T)) / T;
};

const sampler = (days, step) => {
  const every = step || Math.max(1, Math.round(days / 60));
  return (d) => d === 0 || d === days || d % every === 0;
};

/**
 * Day-by-day pledge. `compound` keeps rewards in the order (what CaryPact does by
 * default); turning it off pays them out flat instead.
 */
export const pledgeCurve = ({ pledge, ratePct, termDays, days, compound = true,
  botPrice = 0, monthlyApprPct = 0, step } = {}) => {
  const P = num(pledge);
  const r = num(ratePct) / 100;
  const D = Math.max(1, Math.round(num(days)));
  const keep = sampler(D, step);
  const rows = [];
  for (let d = 0; d <= D; d++) {
    const balance = compound ? P * Math.pow(1 + r, d) : P * (1 + r * d);
    const priceMult = Math.pow(1 + num(monthlyApprPct) / 100, d / 30);
    if (keep(d)) {
      rows.push({
        day: d,
        pledge: P,
        rewards: balance - P,
        balance,
        unlocked: releasedByDay(P, termDays, d),
        usd: balance * num(botPrice) * priceMult,
        botPrice: num(botPrice) * priceMult,
      });
    }
  }
  return rows;
};

/**
 * Claim-as-you-go: rewards taken out as they land plus the principal the term
 * unlocks each day. `breakEvenDay` is the first day that total covers the pledge.
 */
export const moneyBack = ({ pledge, ratePct, termDays, days, step } = {}) => {
  const P = num(pledge);
  const r = num(ratePct) / 100;
  const D = Math.max(1, Math.round(num(days)));
  const keep = sampler(D, step);
  const rows = [];
  let breakEvenDay = null;
  let rewardsAtBreakEven = 0;
  for (let d = 0; d <= D; d++) {
    const rewards = P * r * d;                      // taken out, so nothing compounds
    const unlocked = releasedByDay(P, termDays, d);
    const cashBack = rewards + unlocked;
    if (breakEvenDay === null && P > 0 && cashBack >= P) {
      breakEvenDay = d;
      rewardsAtBreakEven = rewards;
    }
    if (keep(d)) rows.push({ day: d, rewards, unlocked, cashBack, pledge: P });
  }
  const rewardsOnlyDay = r > 0 ? Math.ceil(1 / r) : null;   // if the term never unlocks
  return { rows, breakEvenDay, rewardsAtBreakEven, rewardsOnlyDay, pledge: P };
};

/**
 * BOT Staking Level Income — CaryPact pays your V-level a share of the BOT
 * staking rewards earned underneath you. We never saw its percentage table, but
 * both sides land in our own wallets, so `sharePct` is measured, not guessed.
 */
export const levelIncome = ({ teamPledge, teamRatePct, sharePct, monthlyGrowthPct = 0, months = 12 } = {}) => {
  const share = num(sharePct) / 100;
  const rate = num(teamRatePct) / 100;
  const g = num(monthlyGrowthPct) / 100;
  const M = Math.max(1, Math.round(num(months)));
  const rows = [];
  let cumulative = 0;
  for (let m = 0; m <= M; m++) {
    const teamAtMonth = num(teamPledge) * Math.pow(1 + g, m);
    const perDay = teamAtMonth * rate * share;
    if (m > 0) cumulative += perDay * 30;
    rows.push({ month: m, teamPledge: teamAtMonth, perDay, perMonth: perDay * 30, cumulative });
  }
  return {
    rows,
    perDayBot: num(teamPledge) * rate * share,
    perMonthBot: num(teamPledge) * rate * share * 30,
    totalBot: cumulative,
  };
};

/** A day of CA mining, swapped into BOT at today's two prices. */
export const caToBot = ({ caPerDay, caPrice, botPrice } = {}) => {
  const usd = num(caPerDay) * num(caPrice);
  return { usd, bot: num(botPrice) > 0 ? usd / num(botPrice) : 0 };
};

/**
 * A daily drip of BOT, with each day's arrival pledged the day it lands and
 * compounding from then on.
 */
export const dripPledge = ({ botPerDay, ratePct, days, startingBalance = 0, step } = {}) => {
  const r = num(ratePct) / 100;
  const perDay = num(botPerDay);
  const D = Math.max(1, Math.round(num(days)));
  const keep = sampler(D, step);
  const rows = [];
  let balance = num(startingBalance);
  let added = num(startingBalance);
  for (let d = 0; d <= D; d++) {
    if (d > 0) {
      balance *= 1 + r;
      balance += perDay;
      added += perDay;
    }
    if (keep(d)) rows.push({ day: d, balance, added, rewards: balance - added });
  }
  return rows;
};
